import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';

/** How long a copied secret stays on the clipboard */
export const CLIPBOARD_CLEAR_AFTER = 30_000; // 30s

/**
 * How long a clear may take. Without a recent click or key press, Chrome on Linux leaves the
 * write pending instead of refusing it, so a write that has not settled by then counts as refused.
 */
const CLEAR_TIMEOUT = 2000; // 2s

/** Events that grant the user activation Firefox and Safari require for a clipboard write */
const USER_ACTIVATION = ['pointerdown', 'keydown'] as const;

export interface CopyOptions {
    /** Clear the clipboard this many ms after copying; leave out for values that are not secret */
    clearAfterMs?: number | null;
}

/**
 * Writes to the OS clipboard and wipes copied secrets again after a while (US-2146).
 *
 * The clear is unconditional: checking that the clipboard still holds the secret needs
 * `readText()`, which asks for permission in Chrome and is gated in Firefox and Safari.
 * A browser only lets a focused page write to the clipboard, and Firefox, Safari and Chrome on
 * Linux also want a recent click or key press. A clear the browser refuses, or leaves hanging,
 * is tried again when the user comes back: on focus, on the tab becoming visible, and on their
 * next click or key press.
 *
 * Root-provided, so a pending clear survives the copy button being destroyed on navigation.
 */
@Injectable({ providedIn: 'root' })
export class ClipboardService {
    private readonly document = inject(DOCUMENT);
    private timer: ReturnType<typeof setTimeout> | undefined;
    private pending = false;
    private readonly retry = this.retryWhenBack.bind(this);

    public async copy(value: string, options: CopyOptions = {}): Promise<void> {
        // A refused write leaves the previous secret on the clipboard, so its clear stays pending
        await navigator.clipboard.writeText(value);
        this.cancel();

        if (options.clearAfterMs) {
            this.pending = true;
            this.timer = setTimeout(() => void this.clear(), options.clearAfterMs);
        }
    }

    /** Clear a copied secret right away, e.g. on logout */
    public clearNow(): void {
        if (this.pending) {
            void this.clear();
        }
    }

    private async clear(): Promise<void> {
        this.stopTimer();
        this.stopListening();

        if (!this.document.hasFocus()) {
            this.waitForFocus();
            return;
        }

        try {
            await this.withTimeout(navigator.clipboard.writeText(''));
            this.pending = false;
        } catch {
            // the page lost focus in between: try again once the user is back
            this.waitForFocus();
        }
    }

    private retryWhenBack(): void {
        if (this.document.visibilityState === 'visible') {
            void this.clear();
        }
    }

    private withTimeout(write: Promise<void>): Promise<void> {
        let timer: ReturnType<typeof setTimeout> | undefined;
        const timeout = new Promise<never>((_, reject) => {
            timer = setTimeout(() => reject(new Error('Clipboard write did not settle')), CLEAR_TIMEOUT);
        });

        return Promise.race([write, timeout]).finally(() => clearTimeout(timer));
    }

    private cancel(): void {
        this.pending = false;
        this.stopTimer();
        this.stopListening();
    }

    private waitForFocus(): void {
        this.document.defaultView?.addEventListener('focus', this.retry);
        this.document.addEventListener('visibilitychange', this.retry);
        for (const type of USER_ACTIVATION) {
            this.document.addEventListener(type, this.retry, { capture: true });
        }
    }

    private stopListening(): void {
        this.document.defaultView?.removeEventListener('focus', this.retry);
        this.document.removeEventListener('visibilitychange', this.retry);
        for (const type of USER_ACTIVATION) {
            this.document.removeEventListener(type, this.retry, { capture: true });
        }
    }

    private stopTimer(): void {
        clearTimeout(this.timer);
        this.timer = undefined;
    }
}
